import { useCallback } from "react";
import { useIntl } from "react-intl";

// State/region label per jurisdiction, mirroring `useStateRegion` in shared-ui/kyc.
// Kept here as a plain map so we can hand `AddressManualEntryModal` a getter it can
// call as the selected country changes.
const STATE_REGION_LABELS: Record<string, { id: string; defaultMessage: string }> = {
  US: { id: "kyc: State", defaultMessage: "State" },
  DE: { id: "kyc: State", defaultMessage: "State" },
  CA: { id: "kyc: Province", defaultMessage: "Province" },
  ES: { id: "kyc: Province", defaultMessage: "Province" },
  NL: { id: "kyc: Province", defaultMessage: "Province" },
  BE: { id: "kyc: Province", defaultMessage: "Province" },
  DK: { id: "kyc: Province", defaultMessage: "Province" },
  SE: { id: "kyc: Region", defaultMessage: "Region" },
  NZ: { id: "kyc: Region", defaultMessage: "Region" },
  FR: { id: "kyc: Region", defaultMessage: "Region" },
  IE: { id: "kyc: County", defaultMessage: "County" },
  GB: { id: "kyc: County", defaultMessage: "County" },
  AU: { id: "kyc: Territory", defaultMessage: "Territory" },
};

// Mirrors `getJurisdictionalLabelGetters().getPostCodeLabel` in shared-ui/kyc without
// importing that package (shared-ui ↔ kyc would otherwise form a project-reference cycle).
const POSTAL_CODE_LABELS: Record<string, { id: string; defaultMessage: string }> = {
  US: { id: "kyc: ZIP code", defaultMessage: "ZIP code" },
  IE: { id: "kyc: Eircode", defaultMessage: "Eircode" },
  GB: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  AU: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  CA: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  ES: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  NL: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  BE: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  DK: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  SE: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  NZ: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  DE: { id: "kyc: Postcode", defaultMessage: "Postcode" },
  FR: { id: "kyc: Postcode", defaultMessage: "Postcode" },
};

/**
 * Jurisdiction-aware label getters for the manual-entry modal. This is where the KYC
 * coupling lives, keeping the modal itself presentational.
 */
export const useJurisdictionLabels = () => {
  const intl = useIntl();

  const getStateLabel = useCallback(
    (country: string) => intl.formatMessage(STATE_REGION_LABELS[country] ?? STATE_REGION_LABELS.US),
    [intl]
  );

  const getPostalCodeLabel = useCallback(
    (country: string) =>
      intl.formatMessage(POSTAL_CODE_LABELS[country] ?? { id: "kyc: Postal code", defaultMessage: "Postal code" }),
    [intl]
  );

  return { getStateLabel, getPostalCodeLabel };
};
